import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DayPicker, DateRange } from 'react-day-picker';
import { format, subDays } from 'date-fns';
import { nl, enUS } from 'date-fns/locale';
import { Download, Save, Mail, Calendar, FileText, Loader2, CheckCircle, XCircle, FileSearch } from 'lucide-react';
import { fetchReportSettings, saveReportSettings, generateReport, getReportStatus, fetchReportDetail } from '../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input, Select } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useLang } from '../lib/i18n';
import 'react-day-picker/style.css';

type GenState = 'idle' | 'generating' | 'polling' | 'complete' | 'failed';
type ReportFormat = 'pdf' | 'csv';

export function Reports() {
  const qc = useQueryClient();
  const { t, lang } = useLang();
  const tr = t.reports;

  const [email, setEmail]                   = useState('');
  const [reportInterval, setReportInterval] = useState<'7d' | '14d' | '30d'>('30d');
  const [saved, setSaved]                   = useState(false);

  const [range, setRange]   = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to:   new Date(),
  });
  const [calOpen, setCalOpen] = useState(false);

  const [reportFormat, setReportFormat] = useState<ReportFormat>('pdf');
  const [genState, setGenState] = useState<GenState>('idle');
  const [fileUrl, setFileUrl]   = useState<string | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Detail report (per scanRef) state
  const [detailScanRef, setDetailScanRef] = useState('');
  const [detailState, setDetailState] = useState<'idle' | 'fetching' | 'complete' | 'failed'>('idle');
  const [detailError, setDetailError] = useState<string | null>(null);

  const handleFetchDetail = async () => {
    const ref = detailScanRef.trim();
    if (!ref) return;
    setDetailState('fetching');
    setDetailError(null);
    try {
      const res = await fetchReportDetail(ref, 'en');
      // Decode base64 → Blob → trigger browser download
      const bin = atob(res.pdf_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `verification-${res.scan_ref}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDetailState('complete');
    } catch (e: any) {
      const status = e?.response?.status;
      setDetailError(status === 404 || status === 403 ? tr.detail.notFound : tr.detail.failed);
      setDetailState('failed');
    }
  };

  const { isLoading, data: settings } = useQuery({
    queryKey: ['report-settings'],
    queryFn: fetchReportSettings,
  });

  const initialized = useRef(false);
  useEffect(() => {
    if (settings && !initialized.current) {
      initialized.current = true;
      setEmail(settings.report_email ?? '');
      setReportInterval((settings.report_interval ?? '30d') as '7d' | '14d' | '30d');
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: saveReportSettings,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['report-settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const startPolling = useCallback((id: string) => {
    setGenState('polling');
    pollRef.current = setInterval(async () => {
      try {
        const st = await getReportStatus(id);
        if (st.status === 'complete' && st.file_url) {
          stopPolling();
          setFileUrl(st.file_url);
          setGenState('complete');
          window.open(st.file_url, '_blank');
        } else if (st.status === 'failed') {
          stopPolling();
          setGenState('failed');
          setGenError('PDF generatie mislukt. Probeer opnieuw.');
        }
      } catch (e: any) {
        console.error('Poll error:', e);
      }
    }, 3000);
  }, [stopPolling]);

  const handleGenerate = async () => {
    if (!range?.from || !range?.to) return;
    setGenState('generating');
    setFileUrl(null);
    setGenError(null);
    try {
      const res = await generateReport(
        format(range.from, 'yyyy-MM-dd'),
        format(range.to,   'yyyy-MM-dd'),
        lang,
        reportFormat,
      );
      if (res.format === 'csv') {
        // Trigger immediate browser download
        const blob = new Blob([res.csv], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = res.filename;
        a.click();
        URL.revokeObjectURL(url);
        setGenState('complete');
      } else {
        startPolling(res.report_id);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error ?? e?.message ?? 'Onbekende fout';
      setGenError(msg);
      setGenState('failed');
    }
  };

  const handleReset = () => {
    stopPolling();
    setGenState('idle');
    setFileUrl(null);
    setGenError(null);
  };

  const locale      = lang === 'en' ? enUS : nl;
  const rangeLabel  = range?.from && range?.to
    ? `${format(range.from, 'd MMM yyyy', { locale })} – ${format(range.to, 'd MMM yyyy', { locale })}`
    : tr.download.period;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{tr.title}</h1>
        <p className="text-sm text-gray-500 mt-1">{tr.subtitle}</p>
      </div>

      {/* Auto report settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-brand-600" />
            {tr.autoSend.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500">{tr.autoSend.description}</p>
          <Input id="report-email" label={tr.autoSend.email} type="email"
            placeholder={tr.autoSend.emailPlaceholder}
            value={email} onChange={e => setEmail(e.target.value)} disabled={isLoading} />
          <Select id="report-interval" label={tr.autoSend.interval} value={reportInterval}
            onChange={e => setReportInterval(e.target.value as '7d' | '14d' | '30d')} disabled={isLoading}>
            {(['7d', '14d', '30d'] as const).map(v => (
              <option key={v} value={v}>{tr.autoSend.intervals[v]}</option>
            ))}
          </Select>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => email && saveMutation.mutate({ report_email: email, report_interval: reportInterval, language: lang })}
              loading={saveMutation.isPending} disabled={!email}>
              <Save className="h-4 w-4" />{tr.autoSend.save}
            </Button>
            {saved && <span className="text-sm text-green-600 font-medium">{tr.autoSend.saved}</span>}
          </div>
        </CardContent>
      </Card>

      {/* Manual PDF download */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-brand-600" />
            {tr.download.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500">{tr.download.description}</p>

          {/* Date range picker */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">{tr.download.period}</label>
            <button type="button" onClick={() => genState === 'idle' && setCalOpen(v => !v)}
              className="flex items-center gap-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
              disabled={genState !== 'idle'}>
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className={range?.from ? 'text-gray-900' : 'text-gray-400'}>{rangeLabel}</span>
            </button>
            {calOpen && (
              <div className="border border-gray-200 rounded-xl shadow-lg bg-white p-2 mt-1">
                <DayPicker mode="range" selected={range}
                  onSelect={r => { setRange(r); if (r?.from && r?.to) setCalOpen(false); }}
                  locale={locale} disabled={{ after: new Date() }} />
              </div>
            )}
          </div>

          {/* Format selector */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">{tr.download.format}</label>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden w-fit">
              {(['pdf', 'csv'] as ReportFormat[]).map(f => (
                <button key={f} type="button"
                  onClick={() => { if (genState === 'idle') { setReportFormat(f); handleReset(); } }}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    reportFormat === f
                      ? 'bg-brand-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}>
                  {f === 'pdf' ? tr.download.formatPdf : tr.download.formatCsv}
                </button>
              ))}
            </div>
          </div>

          {/* Status indicators */}
          {genState === 'generating' && (
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
              <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
              <div>
                <p className="text-sm font-medium text-blue-900">{tr.download.generating}</p>
                <p className="text-xs text-blue-700">{tr.download.generatingDesc}</p>
              </div>
            </div>
          )}

          {genState === 'polling' && (
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
              <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
              <div>
                <p className="text-sm font-medium text-blue-900">{tr.download.polling}</p>
                <p className="text-xs text-blue-700">{tr.download.pollingDesc}</p>
              </div>
            </div>
          )}

          {genState === 'complete' && fileUrl && (
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-900">{tr.download.complete}</p>
                  <p className="text-xs text-green-700">{tr.download.completeDesc}</p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <a href={fileUrl} target="_blank" rel="noreferrer"
                  className="text-sm font-medium text-green-700 underline">{tr.download.downloadAgain}</a>
                <button onClick={handleReset} className="text-sm text-gray-500 hover:text-gray-700 ml-4">{tr.download.newReport}</button>
              </div>
            </div>
          )}

          {genState === 'failed' && (
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div className="flex items-center gap-3">
                <XCircle className="h-5 w-5 text-red-600" />
                <div>
                  <p className="text-sm font-medium text-red-900">{tr.download.failed}</p>
                  <p className="text-xs text-red-700">{genError}</p>
                </div>
              </div>
              <button onClick={handleReset} className="text-sm font-medium text-red-700 underline">{tr.download.retry}</button>
            </div>
          )}

          {genState === 'idle' && (
            <Button onClick={handleGenerate} disabled={!range?.from || !range?.to}>
              <Download className="h-4 w-4" />
              {tr.download.generate}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Detail report by scanRef */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSearch className="h-4 w-4 text-brand-600" />
            {tr.detail.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500">{tr.detail.description}</p>
          <Input
            id="detail-scan-ref"
            label={tr.detail.scanRefLabel}
            type="text"
            placeholder={tr.detail.scanRefPlaceholder}
            value={detailScanRef}
            onChange={e => { setDetailScanRef(e.target.value); if (detailState !== 'idle') { setDetailState('idle'); setDetailError(null); } }}
            disabled={detailState === 'fetching'}
          />

          {detailState === 'fetching' && (
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
              <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
              <p className="text-sm font-medium text-blue-900">{tr.detail.fetching}</p>
            </div>
          )}

          {detailState === 'complete' && (
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <p className="text-sm font-medium text-green-900">{tr.detail.complete}</p>
            </div>
          )}

          {detailState === 'failed' && (
            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
              <XCircle className="h-5 w-5 text-red-600" />
              <p className="text-sm font-medium text-red-900">{detailError}</p>
            </div>
          )}

          <Button onClick={handleFetchDetail} loading={detailState === 'fetching'} disabled={!detailScanRef.trim()}>
            <Download className="h-4 w-4" />
            {tr.detail.fetchBtn}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
